import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, Calendar, DollarSign } from "lucide-react";

interface RevenueForecast {
  period: string;
  periodLabel: string;
  renewals: number;
  expectedRevenue: number;
  breakdown: {
    single: { count: number; revenue: number };
    team: { count: number; revenue: number };
    enterprise: { count: number; revenue: number };
  };
}

interface ForecastData {
  forecasts: RevenueForecast[];
  currentMRR: number;
  projectedGrowth: number;
}

export default function RevenueForecastTable() {
  const [selectedPeriod, setSelectedPeriod] = useState("thisWeek");

  const { data: forecastData, isLoading, error } = useQuery({
    queryKey: ['/api/admin/system/revenue-forecast'],
  });

  const periods = [
    { key: "thisWeek", label: "This Week" },
    { key: "nextWeek", label: "Next Week" },
    { key: "thisMonth", label: "This Month" },
    { key: "nextMonth", label: "Next Month" },
    { key: "nextQuarter", label: "Next Quarter" }
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Card className="bg-white rounded-lg shadow-sm border border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            Revenue Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !forecastData) {
    return (
      <Card className="bg-white rounded-lg shadow-sm border border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-medium flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            Revenue Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            Unable to load forecast data
          </div>
        </CardContent>
      </Card>
    );
  }

  const selectedForecast = forecastData.forecasts?.find(f => f.period === selectedPeriod);

  return (
    <Card className="bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition duration-150">
      <CardHeader>
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-emerald-500" />
          Revenue Forecast & Cash Flow Planning
        </CardTitle>
        <CardDescription>
          Expected subscription renewals and revenue projections based on current MRR: {formatCurrency(forecastData.currentMRR || 0)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Period Selection */}
        <div className="mb-6 flex gap-2 flex-wrap">
          {periods.map((period) => (
            <Button
              key={period.key}
              size="sm"
              variant={selectedPeriod === period.key ? "default" : "outline"}
              onClick={() => setSelectedPeriod(period.key)}
            >
              {period.label}
            </Button>
          ))}
        </div>

        {selectedForecast && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-emerald-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Expected Renewals</span>
                </div>
                <div className="text-2xl font-bold text-emerald-900">
                  {selectedForecast.renewals}
                </div>
                <div className="text-xs text-emerald-600">subscriptions</div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">Expected Revenue</span>
                </div>
                <div className="text-2xl font-bold text-blue-900">
                  {formatCurrency(selectedForecast.expectedRevenue)}
                </div>
                <div className="text-xs text-blue-600">{selectedForecast.periodLabel}</div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">Growth Rate</span>
                </div>
                <div className="text-2xl font-bold text-purple-900">
                  +{forecastData.projectedGrowth}%
                </div>
                <div className="text-xs text-purple-600">projected</div>
              </div>
            </div>

            {/* Breakdown Table */}
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-medium">Plan Type</th>
                    <th className="text-center p-3 font-medium">Expected Renewals</th>
                    <th className="text-right p-3 font-medium">Revenue Impact</th>
                    <th className="text-right p-3 font-medium">Per Subscription</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr className="hover:bg-slate-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="font-medium">Single Plan</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700">
                        {selectedForecast.breakdown.single.count}
                      </span>
                    </td>
                    <td className="p-3 text-right font-medium">
                      {formatCurrency(selectedForecast.breakdown.single.revenue)}
                    </td>
                    <td className="p-3 text-right text-slate-600">
                      $29.00
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                        <span className="font-medium">Team Plan</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-700">
                        {selectedForecast.breakdown.team.count}
                      </span>
                    </td>
                    <td className="p-3 text-right font-medium">
                      {formatCurrency(selectedForecast.breakdown.team.revenue)}
                    </td>
                    <td className="p-3 text-right text-slate-600">
                      $49.00
                    </td>
                  </tr>

                  <tr className="hover:bg-slate-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                        <span className="font-medium">Enterprise Plan</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-purple-50 text-purple-700">
                        {selectedForecast.breakdown.enterprise.count}
                      </span>
                    </td>
                    <td className="p-3 text-right font-medium">
                      {formatCurrency(selectedForecast.breakdown.enterprise.revenue)}
                    </td>
                    <td className="p-3 text-right text-slate-600">
                      $199.00
                    </td>
                  </tr>

                  <tr className="bg-slate-50 font-medium">
                    <td className="p-3">Total Forecast</td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-slate-200 text-slate-700">
                        {selectedForecast.renewals}
                      </span>
                    </td>
                    <td className="p-3 text-right text-lg">
                      {formatCurrency(selectedForecast.expectedRevenue)}
                    </td>
                    <td className="p-3 text-right text-slate-600">-</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Calculation Note */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-800">
                <div className="font-semibold mb-2">How Revenue Impact is Calculated:</div>
                <div className="space-y-1">
                  <div>• <strong>Single Plan:</strong> Expected renewals × $29.00 per subscription</div>
                  <div>• <strong>Team Plan:</strong> Expected renewals × $49.00 per subscription</div>
                  <div>• <strong>Enterprise Plan:</strong> Expected renewals × $199.00 per subscription</div>
                </div>
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <div className="text-xs">
                    <strong>Renewal Rate:</strong> Weekly forecasts use 25% of monthly subscription base. 
                    Monthly forecasts use 100% renewal rate. Projections based on current MRR of $1,507.
                  </div>
                </div>
              </div>
            </div>

            {/* Cash Flow Note */}
            <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="text-sm text-amber-800">
                <strong>Cash Flow Planning:</strong> These projections help estimate incoming revenue for operational planning. Actual results may vary based on churn rates and new subscriptions.
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}